<template>
	<li
		:id="`location-card-${location.mmsClubId}`"
		class="result-card border-bottom mx-n2 mx-md-0 p-2"
		:class="{ selected: isSelected }"
		tabindex="0"
		:data-testid="`location-card-${location.mmsClubId}`"
	>
		<div class="image-container position-relative">
			<div
				v-if="location.comingSoon"
				class="badge badge-glass badge-glass-sm badge-glass-dark badge-coming-soon rounded-pill position-absolute mb-1 mb-md-0"
				data-testid="coming-soon"
			>
				{{ location.comingSoon }}
			</div>

			<img
				v-if="smImage"
				:alt="smImage.alt ?? `${location.clubTitle} Exterior`"
				data-testid="thumbnail-sm"
				:src="smImage.path"
				class="result-image rounded-3 mb-1 mb-md-0"
			/>
		</div>

		<div
			class="result-body flex-grow-1 d-flex flex-column justify-content-between overflow-x-hidden"
		>
			<div>
				<div class="result-title h3 mb-0" data-testid="club-title">{{ location.clubTitle }}</div>
				<div class="result-address small mb-1" data-testid="location-info">
					{{ location.street1 }}, {{ location.city }}, {{ location.stateAbbr }} {{ location.zip }}
					<br />
					<a
						v-if="phone && !isClosed"
						:href="`tel:${phone.replace(/\D/g, '')}`"
						data-testid="link-phone"
						>{{ phone }}</a
					>
				</div>
				<div
					v-if="location.comingSoon && isMobileMapView"
					class="d-inline-block d-lg-none"
					data-testid="coming-soon-mobile-map-view"
				>
					<span class="badge rounded-pill rounded-pill-sm border">{{ location.comingSoon }}</span>
				</div>
			</div>

			<div class="d-flex align-items-baseline">
				<div class="small opacity-75">
					<a
						v-if="location.startingAtPriceOverrideLink"
						:href="location.startingAtPriceOverrideLink"
						data-testid="link-day-pass"
						class="join-link"
						v-adobe-data-layer="buyDayPassADLConfig"
						>{{ dayPassText }}
						<span class="visually-hidden">for {{ location.clubTitle }}</span>
					</a>
				</div>
				<a
					v-if="location.locationPagePath"
					:href="location.locationPagePath"
					class="btn btn-dark btn-sm px-2 px-sm-3 px-lg-2 px-xl-3 ms-auto"
					data-testid="link-details"
					v-adobe-data-layer="detailsLinkADLConfig"
				>
					Details <span class="visually-hidden">about {{ location.clubTitle }}</span>
				</a>
			</div>
		</div>
	</li>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { findImage, getStartingAtPriceLinkOverrideText } from '@/util/location';
import type { Location } from 'Types/aemui/find-a-location/findaLocation';

const props = defineProps<{
	location: Location;
	isSelected: boolean;
	isMobileMapView: boolean;
}>();

const smImage = computed(() => findImage(props.location, '16x9'));

const buyDayPassOverrideText = computed(() => getStartingAtPriceLinkOverrideText(props.location));
const ltWorkPhone = computed(() => props.location.phone);
const isClosed = computed(() => {
	return props.location.open === 'closed';
});

const phone = computed(() => {
	if (ltWorkPhone.value) {
		return ltWorkPhone.value;
	} else {
		return '';
	}
});

const dayPassText = computed(() => {
	if (buyDayPassOverrideText.value !== '') {
		return buyDayPassOverrideText.value;
	} else {
		return 'Buy a Day Pass';
	}
});

const detailsLinkADLConfig = {
	name: 'findLocationWork',
	type: 'link',
	title: 'details link',
	link: props.location.locationPagePath
};

const buyDayPassADLConfig = {
	name: 'findLocationWork',
	type: 'link',
	title: 'buy a day pass',
	link: props.location.startingAtPriceOverrideLink
};
</script>

<style lang="scss" scoped>
.result-card {
	.image-container {
		.badge {
			display: block;
			line-height: 16px;

			&-coming-soon {
				bottom: 12px;
				left: 12px;
			}
		}
	}
}

.join-link {
	display: inline-block;
	margin-bottom: 1px;
}

body:not(.find-location-mapview) {
	.result-card {
		@include media-breakpoint-up(md) {
			display: flex;
			gap: 1rem;
		}

		.image-container {
			@include media-breakpoint-up(md) {
				height: 9.5rem;
			}
		}
	}
}

.find-location-mapview {
	.result-card {
		@include media-breakpoint-up(lg) {
			display: flex;
			gap: 1rem;
		}

		.image-container {
			@include media-breakpoint-up(lg) {
				height: 9.5rem;
			}

			.badge {
				display: none;

				@include media-breakpoint-up(lg) {
					display: block;
				}
			}
		}
	}
}
</style>
